import * as React from 'react';
import gql from 'graphql-tag';
import I18n from 'i18n-js';
import classNames from 'classnames';

import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import { flashSuccess } from '@reverbdotcom/commons/src/flash';
import CreateOfferButton, { ListingCreateOfferButtonFragment } from '@reverbdotcom/commons/src/offers/create_offer_button';
import { core_apimessages_Listing_ListingType as ListingType } from '@reverbdotcom/commons/src/rql';
import { ListViewListings as Data } from '../schemas/rql';
import { SHOP_PATH, listingItemPagePath } from '@reverbdotcom/commons/src/url_helpers';
import { isEndedWatchesPath } from './shared/url_helpers';
import AddToCartButton, { AddToCartButtonFragment } from '@reverbdotcom/commons/src/components/add_to_cart_button';
import ConditionDisplay from '@reverbdotcom/commons/src/components/condition_display';
import DynamicImage from '@reverbdotcom/commons/src/components/dynamic_image';
import { DisplayStyle, WatchBadge } from '@reverbdotcom/commons/src/components/watch_badge';
import { WatchParentComponentNames } from '@reverbdotcom/commons/src/event_tracking/watchlist';
import ListingCardNudge from '@reverbdotcom/commons/src/components/listing_card_nudge';
import { InOtherCartsCardNudge, InOtherCartsCardFragment } from '@reverbdotcom/commons/src/components/in_other_carts_card_nudge';
import BumpTag, { shouldShowBumpTag } from '@reverbdotcom/commons/src/components/bump_tag';
import { useUser } from '@reverbdotcom/commons/src/user_hooks';
import { WEB } from '@reverbdotcom/commons/src/constants';
import { buildListingClickEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import ListingShippingDisplay from '@reverbdotcom/commons/src/components/listing_shipping_display';
import { PriceDisplay } from '@reverbdotcom/commons';
import { ListingCardFragment } from '@reverbdotcom/commons/src/components/listing_card';
import { RCStarRating } from '@reverbdotcom/cadence/components';
import ListingCardSignals, { ListingWithSignals } from '@reverbdotcom/commons/src/components/listing_card_signals';
import { ENDED_LISTING_STATES } from './shared/constants';
import { SimilarListingsCTA } from './similar_listings_cta';
import { withRouter, WithRouterProps } from 'react-router';
import { getSimilarListingPageRoute } from './shared/url_helpers';

const DIGITAL_LISTING_UUID = 'e71b6e43-7217-4746-b4e3-64bf708a2664';
const COMPONENT_NAME = 'ListViewRowCard';
const LIVE = 'live';

interface ExternalProps {
  listing: Data.Fragment & ListingWithSignals;
  loading: boolean;
  position?: number;
  trackingQuery?: string;
  showingSold?: boolean;
}

type IProps = ExternalProps & WithRouterProps;

function ListingSignals({ listing, forMobile = false }) {
  return (
    <div className={forMobile ? 'list-view-row-card__mobile-nudges' : 'list-view-row-card__nudges'}>
      {listing.signals ?
        <ListingCardSignals listing={listing} /> :
        <>
          <ListingCardNudge
            freeExpeditedShipping={!!listing.shipping?.freeExpeditedShipping}
            freeShipping={false}
            localPickupOnly={!!listing.shipping?.localPickupOnly}
          />
          <InOtherCartsCardNudge listing={listing} />
        </>
      }
    </div>
  );
}

export function ListViewRowCard(props: IProps) {
  const user = useUser();

  if (props.loading) return <div className={'list-view-row-card--loading'} />;

  const isDigitalListing = !props.listing.categoryUuids ? false : props.listing.categoryUuids.includes(DIGITAL_LISTING_UUID);

  const listingClickEvent = () => {
    const sellerCountryCode = props.listing.shop.address?.country.countryCode;

    const isListingDomesticToBuyer = (sellerCountryCode?: string, buyerCountryCode?: string) => {
      if (!(sellerCountryCode && buyerCountryCode)) { return false; }

      return sellerCountryCode === buyerCountryCode;
    };

    return {
      query: props.trackingQuery,
      ...buildListingClickEvent({
        listing: props.listing,
        componentName: COMPONENT_NAME,
        displayStyle: 'Row',
        position: props.position,
        city: props.listing.shop.address?.locality,
        region: props.listing.shop.address?.region,
        countryCode: sellerCountryCode,
        isListingDomesticToBuyer: isListingDomesticToBuyer(sellerCountryCode, user?.countryCode),
      }),
    };
  };

  const isAuction = props.listing.listingType === ListingType.AUCTION;
  const buyerPriceCents = props.listing.pricing.buyerPrice.amountCents;

  const { name, slug } = props.listing.shop;
  const receivedCount = props.listing.seller?.feedbackSummary?.receivedCount;
  const rollingRatingPercentage = props.listing.seller?.feedbackSummary?.rollingRatingPercentage;
  const rating = rollingRatingPercentage / 100;

  const hasDiscount = !!props.listing.pricing.originalPrice;
  const discountRibbonText = props.listing.pricing.ribbon?.display;

  const openNewTab = user && user.deviceName === WEB;

  if (props.loading) return <div className={'grid-card-row--loading'} />;
  const listingPath = `${listingItemPagePath(props.listing)}${isEndedWatchesPath(props?.location) ? '?show_sold=true' : ''}`;

  return (
    <div className={classNames(
      'list-view-row-card',
      {
        'list-view-row-card--loading': props.loading,
      },
    )}>
      <div className="list-view-row-card__thumbnail">
        <div className="list-view-row-card__thumbnail-container">
          <CoreLink
            className="list-view-row-card__thumbnail-inner"
            to={listingPath}
            target={openNewTab ? '_blank' : undefined}
            clickEvent={listingClickEvent()}
          >
            <DynamicImage src={props.listing?.images[0]?.source} />
          </CoreLink>
          {props.listing.state !== LIVE &&
            <div className="list-view-row-card__sold list-view-row-card__sold--thumb">
              {props.listing.stateDescription}
            </div>
          }
        </div>
        {(shouldShowBumpTag(props.listing)) && (
          <div className="list-view-row-card__bump list-view-row-card__bump--thumb">
            <BumpTag />
          </div>
        )}
      </div>
      <div className="list-view-row-card__info">
        <div className="list-view-row-card__titleblock">
          <div className="list-view-row-card__titleblock-inner">
            {(shouldShowBumpTag(props.listing)) && (
              <div className="list-view-row-card__bump list-view-row-card__bump--titleblock">
                <BumpTag />
              </div>
            )}
            <div className="list-view-row-card__titleblock-watch">
              <WatchBadge
                listing={props.listing}
                displayStyle={DisplayStyle.ICON}
                parentComponentName={WatchParentComponentNames.MarketplaceListViewIcon}
                useToast
              />
            </div>
            <CoreLink
              className="list-view-row-card__title-inner"
              to={listingPath}
              target={openNewTab ? '_blank' : undefined}
              clickEvent={listingClickEvent()}
            >
              <h4 className="list-view-row-card__title">
                {props.listing.title}
              </h4>
            </CoreLink>
            {!isDigitalListing &&
              <div className="list-view-row-card__condition">
                <ConditionDisplay
                  slug={props.listing.condition.conditionSlug}
                  displayName={props.listing.condition.displayName}
                  textOnly
                />
              </div>
            }
          </div>
        </div>

        <div className="list-view-row-card__priceblock">
          <div className="list-view-row-card__priceblock-inner">
            {hasDiscount &&
              <div className="list-view-row-card__original-price">
                <span className="list-view-row-card__original-price-display">
                  {props.listing.pricing.originalPrice.display}
                </span>
                <span className="list-view-row-card__discount">
                  {discountRibbonText}
                </span>
              </div>
            }
            <div className="list-view-row-card__footer__pricing">
              <div className="list-view-row-card__price">
                <PriceDisplay
                  amountCents={props.listing.pricing.buyerPrice.amountCents}
                  currency={props.listing.pricing.buyerPrice.currency}
                  display={props.listing.pricing.buyerPrice.display}
                  auction={isAuction}
                />
                <ListingShippingDisplay
                  listing={props.listing}
                  shopSubtotalForListingCents={buyerPriceCents}
                  showCombinedRate={false}
                  showBadges={false}
                  useCombinedShippingStrikethrough
                />
              </div>
            </div>
            <ListingSignals listing={props.listing} />
          </div>
        </div>

        <ListingSignals listing={props.listing} forMobile />

        <div className="list-view-row-card__shop-info">
          <div className="list-view-row-card__shop-info-inner">
            <div className="list-view-row-card__seller-name">
              <CoreLink
                to={SHOP_PATH.expand({ slug })}
                className="list-view-row-card__seller-inner"
              >
                {name}
              </CoreLink>
            </div>
            <div className="list-view-row-card__seller-location">
              {props.listing.shop.address.displayLocation}
            </div>
            {receivedCount > 0 && (
              <div className="list-view-row-card__seller-rating">
                <div className="list-view-row-card__seller-rating-text">
                  <span>{I18n.t('commons.gridCardRow.sellerRating')}</span>
                </div>
                <RCStarRating
                  rating={rating}
                  count={receivedCount}
                />
              </div>
            )}
          </div>
        </div>
        <div className="list-view-row-card__actions">
          <div className="list-view-row-card__actions-inner">
            <ListViewRowCardActions
              props={props}
              listingClickEvent={listingClickEvent}
              location={props.location}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function ListViewRowCardActions({
  props,
  listingClickEvent,
  location,
}) {
  const isEndedListingOnWatchlist = Object.values(ENDED_LISTING_STATES).includes(props.listing.state) && isEndedWatchesPath(location);

  return (
    <>
      {props.listing.state === LIVE &&
        <div className="list-view-row-card__add-to-cart">
          <AddToCartButton
            listing={props.listing}
            parentComponentName={COMPONENT_NAME}
            listingClickEvent={listingClickEvent}
          />
        </div>
      }
      {isEndedListingOnWatchlist &&
        <div className="list-view-row-card__view-similar-gear">
          <SimilarListingsCTA
            similarListingsLink={getSimilarListingPageRoute(props.listing.id, props.listing.make, props.listing.model)}
            cspLink={props.listing.csp?.webLink?.href}
            hideArrowIcon
          />
        </div>
      }
      <CreateOfferButton
        className="list-view-row-card__make-offer"
        listing={props.listing}
        onOfferSuccess={(message) => {
          flashSuccess(message);
        }}
        parentComponentName={COMPONENT_NAME}
      />
      <WatchBadge
        listing={props.listing}
        parentComponentName={WatchParentComponentNames.MarketplaceListViewAction}
        useToast
      />
    </>
  );
}

export const ListViewRowCardFragment = gql`
  fragment ListViewListings on Listing {
    _id
    id
    categoryUuids
    state
    shop {
      _id
      name
      slug
      preferredSeller
      quickShipper
      quickResponder
      address {
        locality
        region
        displayLocation
        country {
          countryCode
          name
        }
      }
    }
    seller {
      _id
      feedbackSummary {
        receivedCount
        rollingRatingPercentage
      }
    }
    csp {
      _id
      webLink {
        href
      }
    }
    ... AddToCartButtonFields
    ... ListingCardFields
    ... ListingCreateOfferButtonData
    ... InOtherCartsCardData
  }
  ${ListingCardFragment}
  ${AddToCartButtonFragment}
  ${ListingCreateOfferButtonFragment}
  ${InOtherCartsCardFragment}
`;

export default withRouter(ListViewRowCard);
